import React from 'react';

import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HC_exporting from 'highcharts/modules/exporting';
import { colors } from '@alixpartners/ui-components';
import {
  ChartWrapper,
  TooltipWrapper,
  TooltipHeader,
  TooltipItem,
  ColorSymbol,
} from './BenchmarksChart.styles';
import numeral from 'numeral';
import { ISeriesItem } from '../types';
import { HIGHLIGHT_COLOR } from '../utils';
import {
  DECIMALS_FORMATS,
  CURRENCIES_SIGNS,
  UNITS_SIGNS,
} from 'utils/constants';
import { ICurrencyFormat } from 'utils/types';
import { renderToString } from 'react-dom/server';

HC_exporting(Highcharts);

interface IProps {
  series: ISeriesItem[];
  categories: string[];
  axisTitle: string;
  format: ICurrencyFormat;
  isLoading?: boolean;
  isSpend?: boolean;
  isPercentage?: boolean;
  selectedCategory: string | null;
  onCategorySelect(category: string | null): void;
}

export class BenchmarksChart extends React.Component<IProps> {
  chartRef = React.createRef<any>();

  componentDidUpdate(prevProps: IProps) {
    const { isLoading } = this.props;
    const chart = this.getChartInstance();

    if (isLoading !== prevProps.isLoading && chart) {
      if (!isLoading) {
        chart.hideLoading();
      } else {
        chart.showLoading();
      }
    }
  }

  pointFormat = (value: number) => {
    const { isSpend, format, isPercentage } = this.props;
    const decimalFormat = DECIMALS_FORMATS[format.decimal];

    const formattedValue = numeral(value).format(`0,0${decimalFormat}`);

    if (isSpend) {
      if (!isPercentage) {
        return `${CURRENCIES_SIGNS[format.currency]}${formattedValue}${
          UNITS_SIGNS[format.unit]
        }`;
      }

      return `${formattedValue}%`;
    }

    return formattedValue;
  };

  transformSelection = () => {
    const chart = this.getChartInstance();

    if (
      chart &&
      chart.xAxis &&
      chart.xAxis[0] &&
      chart.xAxis[0].plotLinesAndBands
    ) {
      chart.xAxis[0].plotLinesAndBands.forEach((plotBand: any) => {
        plotBand.svgElem.attr({
          transform: 'translate(0, -42) scale(1, 2.054)',
        });
      });
    }
  };

  getPlotBands = () => {
    const { onCategorySelect, categories, selectedCategory } = this.props;
    const selectedIndex = selectedCategory
      ? categories.findIndex((item) => item === selectedCategory)
      : -1;

    return categories.map((category, index) => ({
      from: index - 0.5,
      to: index + 0.5,
      color: index === selectedIndex ? HIGHLIGHT_COLOR : 'transparent',
      events: {
        click: () => {
          const selection =
            index === selectedIndex || category.indexOf('Total') === 0
              ? null
              : category;

          onCategorySelect(selection);
        },
      },
    }));
  };

  getOptions = () => {
    const { axisTitle, series, categories, selectedCategory, format } =
      this.props;

    const decimalFormat = DECIMALS_FORMATS[format.decimal];
    const tooltipFormat = this.pointFormat;

    return {
      chart: {
        type: 'column',
        style: { fontFamily: 'Roboto' },
        marginTop: 40,
        spacingBottom: 40,
        height: 500,
        animation: { duration: 200 },
        events: {
          redraw: () => {
            this.transformSelection();
          },
        },
      },
      exporting: { enabled: false },
      credits: { enabled: false },
      title: { text: '' },
      loading: {
        hideDuration: 500,
        showDuration: 500,
        labelStyle: {
          fontWeight: 'normal',
          letterSpacing: '0.3px',
        },
      },
      xAxis: {
        categories,
        labels: {
          autoRotation: undefined,
          style: {
            color: colors.grey7,
            fontSize: '11px',
            letterSpacing: '0.3px',
            fontWeight: 500,
          },
          // @ts-ignore
          formatter: function () {
            return `<span style="opacity: ${
              // @ts-ignore
              !selectedCategory || this.value === selectedCategory ? 1 : 0.4
              // @ts-ignore
            }">${this.value}</span>`;
          },
        },
        plotBands: this.getPlotBands(),
      },
      yAxis: {
        title: {
          align: 'high',
          text: axisTitle,
          offset: 15,
          rotation: 0,
          y: -20,
          style: {
            color: colors.grey7,
            fontSize: '13px',
            letterSpacing: '0.3px',
          },
        },
        labels: {
          style: {
            color: colors.grey7,
            fontSize: '13px',
            letterSpacing: '0.3px',
          },
          // @ts-ignore
          formatter: function () {
            // @ts-ignore
            return numeral(this.value).format('0,0');
          },
        },
        lineWidth: 1,
        lineColor: colors.grey3,
        tickColor: colors.grey3,
        tickWidth: 1,
        tickLength: 5,
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: colors.white,
        borderWidth: 0,
        borderRadius: 2,
        formatter: function () {
          // @ts-ignore
          const { points } = this as any;
          // @ts-ignore
          const category = this.x;

          return renderToString(
            <TooltipWrapper>
              <TooltipHeader>{category}</TooltipHeader>
              {points.map((point: any, index: number) => (
                <TooltipItem key={index}>
                  <ColorSymbol color={point.color} />
                  <span>
                    {point.series.name}: <b>{tooltipFormat(point.y)}</b>
                  </span>
                </TooltipItem>
              ))}
            </TooltipWrapper>
          );
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0,
          borderWidth: 0,
        },
        series: {
          events: {
            legendItemClick: (e: any) => {
              e.preventDefault();
            },
            click: this.onCategoryClick,
          },
          states: {
            select: { enabled: true, marker: { enabled: true } },
          },
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: 'none',
            style: {
              color: colors.grey7,
              fontSize: '13px',
              letterSpacing: '0.3px',
            },
            // @ts-ignore
            formatter: function () {
              // @ts-ignore
              return numeral(this.y).format(`0,0${decimalFormat}`);
            },
          },
        },
      },
      legend: {
        itemStyle: {
          fontSize: '11px',
          fontWeight: 'normal',
          lineHeight: '10px',
          textTransform: 'uppercase',
          color: colors.grey5,
          letterSpacing: '0.8px',
        },
        symbolRadius: 1,
        symbolHeight: 16,
        symbolWidth: 16,
        lineHeight: 16,
        useHTML: true,
        symbolPadding: 8,
        padding: 22,
      },
      series,
    };
  };

  onCategoryClick = (e: any) => {
    const { selectedCategory, onCategorySelect } = this.props;
    const newCategory = e.point?.category?.toString() || null;

    if (newCategory && newCategory.indexOf('Total') === 0) {
      onCategorySelect(null);

      return;
    }

    if (!selectedCategory) {
      onCategorySelect(newCategory);

      return;
    }

    if (selectedCategory === newCategory) {
      onCategorySelect(null);
    } else {
      onCategorySelect(newCategory);
    }
  };

  getChartInstance = () => {
    return (
      this.chartRef && this.chartRef.current && this.chartRef.current.chart
    );
  };

  render() {
    return (
      <ChartWrapper>
        <HighchartsReact
          highcharts={Highcharts}
          options={this.getOptions()}
          ref={this.chartRef}
        />
      </ChartWrapper>
    );
  }
}
